import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@pv/common/hooks';
import { PvDialog } from '@pv/common/components';
import { TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

interface VenueDataImport {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  errorMessages: string[];
  stagingResults: any;
  storageUrl: string | null;
}

interface DriveImportFormProps {
  venueId: string;
  show: boolean;
  onClose: () => void;
  onCompleted: (venueDataImport: VenueDataImport) => void;
}

const createVenueDataImportMutation = gql`
  mutation CreateVenueDataImportFromDrive($input: CreateVenueDataImportInput!) {
    createVenueDataImport(input: $input) {
      venueDataImport {
        id
        createdAt
        updatedAt
        status
        errorMessages
        stagingResults
        storageUrl
      }
      errors {
        message
      }
    }
  }
`;

export const DriveImportForm = ({
  venueId,
  show,
  onClose,
  onCompleted,
}: DriveImportFormProps) => {
  const [storageUrl, setStorageUrl] = useState('');
  const [locationName, setLocationName] = useState('');

  const [createVenueDataImport, { loading }] = useMutation(
    createVenueDataImportMutation,
    {
      onNoErrorsCompleted: (response: any) => {
        const newImport = response.createVenueDataImport.venueDataImport;
        onCompleted(newImport);
        enqueueSnackbar('Drive import staging started', { variant: 'success' });
        setStorageUrl('');
        setLocationName('');
        onClose();
      },
      onSomeErrorsCompleted: (resp: any) => {
        resp.createVenueDataImport.errors.forEach(
          (error: { message: string }) => {
            enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
          }
        );
      },
    }
  );

  const handleSubmit = () => {
    createVenueDataImport({
      variables: {
        input: { venueId, storageUrl, locationName: locationName.trim() || null },
      },
    });
  };

  return (
    <PvDialog
      open={show}
      onClose={onClose}
      title="Import from Cloud Storage"
      confirmButtonText="Start Staging"
      onConfirm={handleSubmit}
      loading={loading}
      confirmDisabled={!storageUrl.trim()}
    >
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Enter the share link of the drive folder containing the import files. The
        folder must be shared with the service account.
      </Typography>
      <TextField
        fullWidth
        label="URL"
        placeholder="https://drive.google.com/drive/folders/..."
        value={storageUrl}
        onChange={(e) => setStorageUrl(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Location Name (optional)"
        placeholder="Leave blank to use venue name"
        value={locationName}
        onChange={(e) => setLocationName(e.target.value)}
        variant="outlined"
        size="small"
        helperText="Must match the location value in your spreadsheet exactly"
      />
    </PvDialog>
  );
};
